import { http, HttpResponse, RequestHandler } from 'msw';
import {
  ApiContractor,
  ApiEntity,
  ApiWorkOrder,
  ApiWorkOrderRequest,
} from '@allbin/mobilix-api-client';
import { DateTime } from 'luxon';
import { v4 } from 'uuid';
import { tdContractors } from '../../testdata/contractors';
import { tdApiWorkorders } from '../../testdata/workorders';
import { tdGetEntities } from '../../testdata/entities';
import { tdUserIdCreator } from '../../testdata/users';
import { MAINTENANCE_DISTRICT_KEY } from '../../helpers/road_authority_helpers';

// "Send to the road authority" (HK-1117): three Trafikverket districts as
// road_authority contractors, a maintenance district on every stop except the
// first (one of them, Storuman, without a recipient), and a POST /workorders that behaves like the API with
// auto_assign=true&require_match=true (422 with unmatched_entities when a
// stop has no district; otherwise one order per district).
// Open with `?msw_scenario=road-authority`.

const districts = ['Umeå', 'Skellefteå', 'Lycksele'];
// Storuman has no road-authority contractor, so a stop there passes the
// dialog's district check and gets the API's 422 instead.
const stopDistricts = [...districts, 'Storuman'];

const meta = (): ApiContractor['meta'] => ({
  created_at: DateTime.utc().toISO(),
  created_by: tdUserIdCreator,
  updated_at: DateTime.utc().toISO(),
});

const roadAuthorities: ApiContractor[] = districts.map((district, i) => ({
  id: `0f5d1c00-0000-4000-8000-00000000000${i + 1}`,
  name: `Trafikverket driftområde ${district}`,
  tenant_id: 'vl',
  kind: 'road_authority',
  email: `driftomrade${i + 1}@example.com`,
  filterset_id: `41e1315a-68ae-4c01-965f-678d0d2f9d6${i}`,
  meta: meta(),
}));

let entities: ApiEntity[] | undefined;
const getEntities = (): ApiEntity[] => {
  if (!entities) {
    entities = tdGetEntities().map((entity, i) =>
      i === 0
        ? entity
        : {
            ...entity,
            properties: {
              ...entity.properties,
              [MAINTENANCE_DISTRICT_KEY]:
                stopDistricts[i % stopDistricts.length],
            },
          },
    );
  }
  return entities;
};

const districtOf = (entityId: string): string | undefined =>
  getEntities().find((e) => e.id === entityId)?.properties[
    MAINTENANCE_DISTRICT_KEY
  ] as string | undefined;

const toWorkOrder = (
  request: ApiWorkOrderRequest,
  overrides: Partial<ApiWorkOrderRequest>,
): ApiWorkOrder => ({
  ...request,
  ...overrides,
  id: v4(),
  tenant_id: 'vl',
  meta: meta(),
});

export const roadAuthorityScenario: RequestHandler[] = [
  http.get('https://api.mobilix.dev.allbin.se/contractors', () =>
    HttpResponse.json([...tdContractors, ...roadAuthorities]),
  ),
  http.get('https://api.mobilix.dev.allbin.se/entities', () =>
    HttpResponse.json(getEntities()),
  ),
  http.post(
    'https://api.mobilix.dev.allbin.se/workorders',
    async ({ request }) => {
      const params = new URL(request.url).searchParams;
      const body = (await request.json()) as ApiWorkOrderRequest;
      const candidates = (
        body.contractors.length > 0
          ? body.contractors
          : roadAuthorities.map((c) => c.id)
      ).filter((id) => roadAuthorities.some((c) => c.id === id));
      if (params.get('auto_assign') !== 'true' || candidates.length === 0) {
        // Ordinary create, or auto-assign among ordinary contractors only:
        // one order as before, matching is only mocked for road authorities.
        const created = toWorkOrder(body, {});
        tdApiWorkorders.push(created);
        return HttpResponse.json([created]);
      }
      const byDistrict = new Map<string, string[]>();
      const unmatched: string[] = [];
      body.entities.forEach((entityId) => {
        const district = districtOf(entityId);
        const contractor = roadAuthorities.find(
          (c) =>
            candidates.includes(c.id) && c.name.endsWith(district ?? '\u0000'),
        );
        if (!contractor) {
          unmatched.push(entityId);
          return;
        }
        byDistrict.set(contractor.id, [
          ...(byDistrict.get(contractor.id) ?? []),
          entityId,
        ]);
      });
      if (params.get('require_match') === 'true' && unmatched.length > 0) {
        return HttpResponse.json(
          {
            message: `${unmatched.length} entities matched no candidate contractor`,
            unmatched_entities: unmatched,
          },
          { status: 422 },
        );
      }
      const orders = Array.from(byDistrict.entries()).map(
        ([contractorId, entityIds]) =>
          toWorkOrder(body, {
            contractors: [contractorId],
            entities: entityIds,
          }),
      );
      if (unmatched.length > 0) {
        orders.push(
          toWorkOrder(body, { contractors: [], entities: unmatched }),
        );
      }
      tdApiWorkorders.push(...orders);
      return HttpResponse.json(orders);
    },
  ),
];
